import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BlueButtonBig from '../../components/BlueButtonBig';
import ContactMenu from '../../components/ContactMenu';
import Footer from '../../components/Footer';
import FormErrorMessage from '../../components/FormErrorMessage';
import FormInput from '../../components/FormInput';
import FormTextArea from '../../components/FormTextArea';
import Menu from '../../components/Menu/Menu';
import SecondaryButtonBig from '../../components/SecondaryButtonBig';
import Spinner from '../../components/Spinner';
import withRecaptcha from '../../components/withRecaptcha';
import { useAlert } from '../../context/AlertProvider';
import { getToken } from '../../helpers/auth';
import localizedRoute from '../../utils/localizedRoute';
import MyHead from '../../components/MyHead';
import FileInput from '../../components/FileInput';
import get from 'lodash/get';
import { serialize } from 'object-to-formdata';
import Link from 'next/link';

interface IContactData {
  name: string;
  email: string;
  object: string;
  message: string;
  file: File;
  newsletter: boolean;
  contact: boolean;
}

const Contacts: React.FC = () => {
  const alert = useAlert();
  const token = getToken();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  const {
    formState: { errors },
    register,
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<IContactData>({
    defaultValues: {
      name: '',
      email: '',
      object: '',
      message: '',
      file: undefined,
      newsletter: false,
      contact: false
    }
  });

  useEffect(() => {
    register('message', {
      required: t('REQUIRED_FIELD') as string
    });

    register('file', {
      required: t('REQUIRED_FIELD') as string
    });
  }, []);

  const onSubmit = async (values: IContactData, event: any) => {
    setLoading(true);

    // await validateRecaptchaToken({
    //   token,
    //   recaptchaToken: await getRecaptchaToken()
    // });

    const response = await fetch('/api/sendgrid', {
      method: 'POST',
      body: serialize({
        email: values.email,
        fullname: values.name,
        subject: values.object,
        message: values.message,
        formName: 'private',
        file: values.file,
        currentLanguage: i18n.language
      })
    });

    if (!response.ok) {
      alert.error(t('GENERIC_ERROR'));
      setLoading(false);
      return;
    }

    reset();
    event.target.reset();

    setLoading(false);
    alert.show(t('SEND_FORM_SUCCESS'));
  };

  const values = watch();

  return (
    <>
      <MyHead />
      <Menu />
      <div className="py-12 bg-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <ContactMenu actual_page={'contacts'} />
          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <div className="flex items-left justify-between mb-3 flex-col md:flex-row md:items-center">
              <p>{t('CONTACT_US_DESC')}</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <FormInput
                  id="name"
                  type="text"
                  placeholder={t('NAME')}
                  invalid={Boolean(errors.name)}
                  {...{
                    ...register('name', {
                      required: t('REQUIRED_FIELD') as string
                    })
                  }}
                />

                <FormErrorMessage errors={errors} name="name" />
              </div>

              <div>
                <FormInput
                  id="email"
                  type="email"
                  placeholder={t('MAIL_ADDRESS')}
                  invalid={Boolean(errors.email)}
                  {...register('email', {
                    required: t('REQUIRED_FIELD') as string,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                      message: t('INVALID_EMAIL')
                    }
                  })}
                />

                <FormErrorMessage errors={errors} name="email" />
              </div>

              <div>
                <FormInput
                  id="object"
                  type="text"
                  placeholder={t('SUBJECT')}
                  invalid={Boolean(errors.object)}
                  {...{
                    ...register('object', {
                      required: t('REQUIRED_FIELD') as string
                    })
                  }}
                />

                <FormErrorMessage errors={errors} name="object" />
              </div>

              <div>
                <FormTextArea
                  id="message"
                  placeholder={t('MESSAGE')}
                  invalid={Boolean(errors.message)}
                  value={values.message}
                  onChange={({ currentTarget }) =>
                    setValue('message', currentTarget.value, {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="message" />
              </div>

              <div>
                <FileInput
                  id="file"
                  label="File da caricare"
                  onChange={({ currentTarget }) =>
                    setValue('file', currentTarget.files![0], {
                      shouldValidate: true
                    })
                  }
                />

                <FormErrorMessage errors={errors} name="file" />
              </div>

              <div
                className={`relative flex items-center mt-2 py-2 px-3 border rounded ${
                  errors.newsletter ? ' border-red-500' : 'border-transparent'
                }`}
              >
                <input
                  id="contact"
                  aria-describedby="privacy-description"
                  type="checkbox"
                  className="focus:outline-none h-4 w-4 text-primary border-gray-300 rounded"
                  {...register('contact', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />
                <div className="ml-2 text-sm">
                  <label htmlFor="contact">
                    <div
                      id="newsletter-description"
                      className="text-gray-500 cursor-pointer"
                    >
                      {t('FOOTER_NEWSLETTER_PRIVACY')}
                      <a
                        href={`https://www.iubenda.com/privacy-policy/${t(
                          'IUBENDA.POLICY_ID'
                        )}`}
                        className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                        title="Privacy Policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('PRIVACY_TITLE')}
                      </a>
                    </div>
                  </label>
                </div>
              </div>
              <div className="mt-6 max-w-lg">
                {loading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="w-full">
                    <SecondaryButtonBig text={t('SEND')} />
                  </button>
                )}
              </div>
            </form>
          </div>
          <div className="bg-white py-8 px-6 rounded-lg mb-6">
            <h3 className="mr-5 text-3xl font-sans mb-3">
              {t('CONTACT_US_RETURN_TITLE')}
            </h3>
            <p className="mb-10">{t('CONTACT_US_RETURN_DESC')}</p>
            <Link href={localizedRoute('/return')}>
              <BlueButtonBig text={t('CONTACT_US_RETURN_BTN')} />
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default withRecaptcha(Contacts);
